import _omit from 'lodash/omit'
import _pick from 'lodash/pick'
import { usePlausible } from 'next-plausible'
import { Router, useRouter } from 'next/router'
import type { ParsedUrlQuery } from 'node:querystring'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { match } from 'ts-pattern'

import {
  DOMAIN,
  GETSAFE_TYPEFORM_DOMAIN,
  TRACK_URL,
  WEB_BUYING_FLOW_DOMAIN,
  isDev,
} from '@/config/env'
import * as RUMService from '@/services/RUM'
import { reportError } from '@/services/errorReporting'
import { getFacebookParams, getOpprefParams } from '@/utils/cookies'
import { getLocaleParts } from '@/utils/locale'
import { getFlowNameFromPath, normalizePathForTracking } from '@/utils/url'

type AnalyticsContextValue = {
  track: (event: string, properties?: Record<string, any>) => void
  deviceCategory: 'mobile' | 'desktop'
  loadSegmentAnalytics: () => Promise<void>
  appendQueryParametersToAnchorTags: () => void
}

const AnalyticsContext = createContext<AnalyticsContextValue | undefined>(undefined)

const PII_PROPERTIES = ['email', 'phone_number']

const getMarketingParamsFromQuery = (query: ParsedUrlQuery) => {
  const marketingFields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'gbraid', 'gclid']
  return _pick(query, marketingFields)
}

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter()
  const { locale, query, asPath } = router
  const plausible = usePlausible()
  const [, market] = getLocaleParts(locale)
  const [deviceCategory, setDeviceCategory] = useState<'mobile' | 'desktop'>('desktop')
  const hasTrackedInitialPageLoad = useRef(false)
  const externalReferrer = useRef<string | null>(null)

  const appendQueryParametersToAnchorTags = useCallback(() => {
    document.querySelectorAll('a').forEach((a) => {
      if (
        a.href.includes(DOMAIN) ||
        a.href.includes(WEB_BUYING_FLOW_DOMAIN) ||
        a.href.includes(GETSAFE_TYPEFORM_DOMAIN)
      ) {
        const searchParams = new URL(a.baseURI).searchParams
        const newHref = new URL(a.href)

        for (const [key, value] of searchParams.entries()) {
          if (!newHref.searchParams.has(key)) {
            newHref.searchParams.append(key, value)
          }
        }

        a.href = newHref.toString()
      }
    })
  }, [])

  const loadSegmentAnalytics = useCallback(async () => {
    if (typeof window !== 'undefined') {
      const newDeviceCategory = match(window.matchMedia('(max-width: 767px)').matches)
        .returnType<'mobile' | 'desktop'>()
        .with(true, () => 'mobile')
        .with(false, () => 'desktop')
        .exhaustive()
      setDeviceCategory(newDeviceCategory)

      // Capture external referrer only once on initial load
      // https://developer.mozilla.org/en-US/docs/Web/API/Document/referrer
      if (externalReferrer.current === null && document.referrer) {
        externalReferrer.current = document.referrer
      }
    }
    appendQueryParametersToAnchorTags()
  }, [appendQueryParametersToAnchorTags])

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (url) {
        const marketingParams = getMarketingParamsFromQuery(query)
        const fbParams = getFacebookParams()
        const opprefParams = getOpprefParams()

        const { normalizedPath, utmParams } = normalizePathForTracking(url, locale)
        const flowName = getFlowNameFromPath(normalizedPath)

        void fetch(TRACK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            event: 'page',
            properties: {
              channel: 'web',
              cms: 'sanity',
              deviceCategory,
              ...(flowName ? { flowName, pageType: 'questionnaire' } : {}),
              ...marketingParams,
              ...utmParams,
              ...fbParams,
              ...opprefParams,
              market,
              path: normalizedPath,
              externalReferrer: externalReferrer.current ?? document.referrer,
            },
            context: {
              locale,
            },
          }),
        }).catch(reportError)
      }
    }

    if (!hasTrackedInitialPageLoad.current) {
      handleRouteChange(asPath)
      hasTrackedInitialPageLoad.current = true
    }

    Router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [query, deviceCategory, market, locale, asPath])

  const track = (event: string, properties?: Record<string, any>) => {
    if (typeof window === 'undefined') {
      return
    }

    const marketingParams = getMarketingParamsFromQuery(query)

    const fbParams = getFacebookParams()

    const opprefParams = getOpprefParams()

    const { normalizedPath, utmParams } = normalizePathForTracking(asPath, locale)

    const flowName = getFlowNameFromPath(asPath)

    const info = {
      channel: 'web',
      cms: 'sanity',
      ...(flowName ? { flowName } : {}),
      path: normalizedPath,
      ...marketingParams,
      ...properties,
      ...fbParams,
      ...utmParams,
      ...opprefParams,
      market,
      deviceCategory,
    }

    const nonPiiInfo = _omit(info, PII_PROPERTIES)

    plausible(event, { props: nonPiiInfo })

    RUMService.addAction(event, nonPiiInfo)

    const payload = {
      properties: info,
      context: {
        locale,
      },
    }

    void fetch(TRACK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ event, ...payload }),
    }).catch(reportError)

    if (isDev()) {
      // eslint-disable-next-line no-console
      console.log(`Track: ${event}, ${JSON.stringify(nonPiiInfo)}`)
    }
  }

  return (
    <AnalyticsContext.Provider
      value={{
        track,
        deviceCategory,
        loadSegmentAnalytics,
        appendQueryParametersToAnchorTags,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  )
}

// Create an analytics hook that we can use with other components.
export const useAnalytics = () => {
  const result = useContext(AnalyticsContext)
  if (!result) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider')
  }
  return result
}
